import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { FormsModule } from '@angular/forms';
import { CasheringService } from '../../../domain/services/cashering/cashering.service';
import { CasheringBasketStore } from '../../../domain/services/cashering/cashering-basket-store.service';
import { AuthenticationService } from '../../../domain/services/auth/authentication.service';
import { AccountService } from '../../../domain/services/account/account.service';
import { PropertyService } from '../../../domain/services/property/property.service';
import { forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { EnrollmentType, EnrollmentTypeIntMap, EnrollmentTypeStrMap } from '../../../domain/enums/enrollment-type.enum';
import {
  SearchResultsDialogComponent,
  SearchResultRow
} from '../components/search-results-dialog/search-results-dialog.component';
import { ReturnDetailComponent } from '../return-detail/return-detail.component';
import { ReturnSummaryDto, ReturnStatus as ReturnStatusEnum } from '../../../domain/models/return.model';
import { ReturnStatus } from '../../../domain/models/cashering.model';
import { AccountModel } from '../../../domain/models/account.model';
import {
  PaymentType,
  PaymentMethodType,
  PaymentMethodModel,
  PaymentRequestDto,
  BasketDto
} from '../../../domain/models/payment.model';
import { v4 as uuidv4 } from 'uuid';
import { RemoveFromCartDialogComponent } from '../components/remove-from-cart-dialog/remove-from-cart-dialog.component';
import { PaymentMethodCheckComponent } from '../components/payment-method-check/payment-method-check.component';
import { PaymentMethodCashComponent } from '../components/payment-method-cash/payment-method-cash.component';
import { PaymentMethodDebitComponent } from '../components/payment-method-debit/payment-method-debit.component';
import { GlobalVariablesService } from '../../../domain/services/admin/global-variables.service';
import { CashDrawerService } from '../../../domain/services/cash-drawer/cash-drawer.service';
import { CashDrawer, CashDrawerSummary, Transfer } from '../../../domain/models/cash-drawer.model';
import { OpenDrawerDialogComponent } from '../dialogs/open-drawer-dialog.component';
import { CloseDrawerDialogComponent } from '../dialogs/close-drawer-dialog.component';

@Component({
  selector: 'app-cashering-home',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatRadioModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatPaginatorModule,
    RouterLink,
    FormsModule,
    PaymentMethodCheckComponent,
    PaymentMethodCashComponent,
    PaymentMethodDebitComponent
  ],
  templateUrl: './cashering-home.component.html',
  styleUrls: ['./cashering-home.component.css']
})
export class CasheringHomeComponent implements OnInit, OnDestroy {
  // ===== View state =====
  currentView: 'returns' | 'basket' = 'returns';

  // ===== Customer / search state =====
  customerId!: number;
  customerName: string = '';
  hasCustomer: boolean = false;

  // Search controls
  searchQuery: string = '';
  filterYear: number | '' = '';
  filterMonth: string = '';
  filterStatus: string = '';

  months: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  availableYears: number[] = [];

  /**
   * How far under the amount due a cash payment may still be accepted
   * (payment_threshold_under). Zero until loaded, so a failed fetch is strict rather than
   * accidentally generous.
   */
  underThreshold = 0;
  /** How far over the amount due a cashier may keep instead of giving change (payment_threshold_over). */
  overThreshold = 0;
  /**
   * Cashier-entered over/short (QA 2026-10-04): + = overpayment kept instead of given back as
   * change (e.g. a check written for more), - = shortfall accepted. Null when not used.
   */
  overShort: number | null = null;
  /** Over/short entry is shown (its value may still be blank = 0). */
  showOverShort = false;

  // All returns for the selected customer (shown in the returns table)
  allReturns: ReturnSummaryDto[] = [];
  // allReturns after the Year / Month / Status filters
  filteredReturns: ReturnSummaryDto[] = [];
  // Paged slice of filteredReturns shown in the table
  pagedReturns: ReturnSummaryDto[] = [];
  pageSize: number = 10;
  pageIndex: number = 0;

  // Returns added to the basket (shown in basket view)
  basketItems: ReturnSummaryDto[] = [];

  // ===== Basket / payment state =====
  selectedReturns: ReturnSummaryDto[] = [];
  paymentType: PaymentType = PaymentType.WALK_IN;
  paymentMethods: PaymentMethodModel[] = [];

  // Active basket ID from the backend
  basketId: number | null = null;
  isProcessing: boolean = false;
  isLoadingReturns: boolean = false;
  isSearching: boolean = false;
  isCreatingBasket: boolean = false;
  hasLoadError: boolean = false;

  // Track expanded state per return card (basket view)
  expandedReturns: Set<number> = new Set();

  // Returns table columns
  returnsDisplayedColumns: string[] = [
    'year', 'month', 'type', 'frequency', 'totalAmountDue', 'status', 'actions'
  ];

  PaymentType = PaymentType;
  PaymentMethodType = PaymentMethodType;
  ReturnStatus = ReturnStatus;
  ReturnStatusEnum = ReturnStatusEnum;

  // ===== Cash drawer state =====
  currentDrawer: CashDrawer | null = null;
  pastDrawers: CashDrawerSummary[] = [];
  pendingTransfers: Transfer[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private casheringService: CasheringService,
    private basketStore: CasheringBasketStore,
    private authService: AuthenticationService,
    private accountService: AccountService,
    private propertyService: PropertyService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private drawerSvc: CashDrawerService,
    private globalVars: GlobalVariablesService
  ) {
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 5; y--) {
      this.availableYears.push(y);
    }
  }

  ngOnInit(): void {
    this.globalVars.getAll().subscribe({
      next: vars => {
        this.underThreshold = Number(vars['payment_threshold_under'] ?? 0) || 0;
        this.overThreshold = Number(vars['payment_threshold_over'] ?? 0) || 0;
      },
      error: () => { /* non-blocking; stays 0, which means exact payment only */ },
    });
    this.loadDrawer();

    let cameFromRoute = false;
    // Pre-populate from route params if coming from a customer context
    this.route.params.subscribe(params => {
      if (params['customerId']) {
        cameFromRoute = true;
        this.customerId = +params['customerId'];
        this.hasCustomer = !isNaN(this.customerId) && this.customerId > 0;
        if (this.hasCustomer) {
          this.initBasketAndLoadReturns();
        }
      }
    });

    this.route.queryParams.subscribe(params => {
      this.customerName = params['customerName'] || '';
      // Arriving with only the id (e.g. "Go to payments" after a receipt) left the filter chip
      // empty, so the returns list had no customer context (QA 2026-10-04). Look the name up.
      if (!this.customerName && this.hasCustomer) {
        this.accountService.getById(this.customerId).subscribe({
          next: a => { if (!this.customerName) this.customerName = this.accountLabel(a); },
          error: () => { /* chip stays hidden; returns still load */ },
        });
      }
    });

    // No customer in the route: restore the session basket if one is in progress, so
    // navigating away from cashering and back does not lose the basket.
    if (!cameFromRoute && !this.hasCustomer && this.basketStore.hasBasket()) {
      this.restoreBasketFromStore();
    }
  }

  ngOnDestroy(): void {
    // Persist the basket for the session instead of deleting it, so it survives
    // navigation. It is cleared only on checkout or an explicit "clear customer".
    this.persistBasket();
  }

  /** Reload a session basket (customer, basket id, items) saved before navigation. */
  private restoreBasketFromStore(): void {
    const saved = this.basketStore.get();
    if (!saved) {
      return;
    }
    this.customerId = saved.customerId;
    this.customerName = saved.customerName;
    this.basketId = saved.basketId;
    this.basketItems = [...saved.items];
    this.selectedReturns = [...saved.items];
    this.hasCustomer = true;
    this.loadOutstandingReturns();
  }

  /** Save the current basket to the session store (no-op when there is nothing to keep). */
  private persistBasket(): void {
    if (this.hasCustomer && this.basketId !== null && this.basketItems.length > 0) {
      this.basketStore.save({
        customerId: this.customerId,
        customerName: this.customerName,
        basketId: this.basketId,
        items: this.basketItems
      });
    }
  }

  private getCurrentUserId(): string | undefined {
    return this.authService.getCurrentUser()?.id;
  }

  // ===== Cash drawer =====

  loadDrawer(): void {
    this.drawerSvc.current().subscribe({
      next: d => this.currentDrawer = d ?? null,
      error: () => this.currentDrawer = null, // 204/none
    });
    this.drawerSvc.mine().subscribe(list => this.pastDrawers = list.filter(d => d.status === 'CLOSED'));
    this.drawerSvc.pendingIncoming().subscribe({
      next: t => this.pendingTransfers = t,
      error: () => this.pendingTransfers = []
    });
  }

  openDrawer(): void {
    this.dialog.open(OpenDrawerDialogComponent, { width: '480px' }).afterClosed().subscribe(body => {
      if (body) this.drawerSvc.open(body).subscribe(() => this.loadDrawer());
    });
  }

  /** Fix a drawer opened against the wrong department or float. Server refuses once it is worked. */
  editDrawer(): void {
    const d = this.currentDrawer;
    if (!d) return;
    this.dialog.open(OpenDrawerDialogComponent, { width: '480px', data: { drawer: d } })
      .afterClosed().subscribe(body => {
        if (!body) return;
        this.drawerSvc.edit(d.id, body).subscribe({
          next: () => { this.snackBar.open('Drawer updated', 'Dismiss', { duration: 4000 }); this.loadDrawer(); },
          // 409 when a payment landed between the page load and the save.
          error: e => this.snackBar.open(e?.error?.message ?? 'Could not update this drawer', 'Dismiss', { duration: 7000 }),
        });
      });
  }

  closeDrawer(id: number): void {
    this.dialog.open(CloseDrawerDialogComponent, { width: '560px', data: { drawerId: id } }).afterClosed().subscribe(body => {
      if (body) this.drawerSvc.close(id, body).subscribe(() => {
        this.drawerSvc.settlementPdf(id).subscribe(blob => this.downloadPdf(blob, `drawer-${id}-settlement.pdf`));
        this.loadDrawer();
      });
    });
  }

  acceptTransfer(t: Transfer): void {
    this.drawerSvc.acceptTransfer(t.id, []).subscribe(() => this.loadDrawer());
  }

  rejectTransfer(t: Transfer): void {
    this.drawerSvc.rejectTransfer(t.id).subscribe(() => this.loadDrawer());
  }

  private downloadPdf(blob: Blob, name: string): void {
    // Force the PDF MIME type (responseType:'blob' can yield an empty type) and append the
    // anchor to the DOM with a deferred revoke — revoking synchronously races the browser's
    // async download and drops the .pdf filename (saves a UUID, no extension).
    const file = new Blob([blob], { type: 'application/pdf' });
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
  }

  // ===== Search =====

  searchReturns(): void {
    const query = this.searchQuery.trim();
    if (!query) {
      this.snackBar.open('Please enter a customer name or ID to search.', 'Dismiss', {
        duration: 3000
      });
      return;
    }

    this.isSearching = true;
    this.hasLoadError = false;
    this.hasCustomer = false;
    this.allReturns = [];
    this.filteredReturns = [];
    this.pagedReturns = [];
    this.basketItems = [];

    this.accountService.getAll().subscribe({
      next: (accounts: AccountModel[]) => {
        const matches = accounts.filter(a => this.accountMatches(a, query));

        if (matches.length === 0) {
          this.isSearching = false;
          this.snackBar.open('No customer found matching that search.', 'Dismiss', {
            duration: 4000
          });
          return;
        }

        // Single match: open it directly. Multiple matches: present them in a modal
        // (id, name, account type, properties) so the cashier picks the right one.
        if (matches.length === 1) {
          this.isSearching = false;
          this.selectAccount(matches[0]);
          return;
        }

        this.openResultsModal(matches, query);
      },
      error: (err: any) => {
        console.error('Error searching accounts:', err);
        this.isSearching = false;
        this.hasLoadError = true;
        this.snackBar.open('Error searching for customers. Please try again.', 'Dismiss', {
          duration: 5000,
          panelClass: ['snack-error']
        });
      }
    });
  }

  /**
   * Matches an account against the query across every useful field — name, email,
   * phone, the account address and the rental property address, business/company
   * names, and the account id. Phone matching ignores formatting.
   */
  private accountMatches(a: AccountModel, query: string): boolean {
    const q = query.toLowerCase().trim();
    if (!q) {
      return false;
    }
    const digits = q.replace(/\D/g, '');
    const phoneFields = [a.phoneNumber, a.primaryContactPhoneNumber];
    if (digits.length >= 3 && phoneFields.some(p => p && p.replace(/\D/g, '').includes(digits))) {
      return true;
    }
    const prop = a.rentalProperty;
    const haystack = [
      a.id != null ? String(a.id) : '',
      `${a.firstName || ''} ${a.lastName || ''}`,
      a.entityName, a.printedName, a.personName, a.companyName,
      a.email, a.primaryContactEmail,
      a.streetAddress, a.aptUnitSuite, a.city, a.zipCode,
      a.businessLegalName, a.businessDbaName, a.webSiteUrl,
      a.primaryContactFirstName, a.primaryContactLastName,
      prop?.rentalPropertyStreetAddress, prop?.aptUnitSute, prop?.city, prop?.zipCode, prop?.businessName,
    ]
      .filter(Boolean)
      .map(v => String(v).toLowerCase());
    return haystack.some(field => field.includes(q));
  }

  /** Human-readable label for an account chip. */
  accountLabel(a: AccountModel): string {
    if (a.firstName && a.lastName) {
      return `${a.firstName} ${a.lastName}`;
    }
    return a.entityName || a.printedName || a.companyName || a.personName || String(a.id);
  }

  /** Open a customer's returns (chosen from the search results modal). */
  selectAccount(account: AccountModel): void {
    if (!account.id) {
      return;
    }
    this.customerId = account.id;
    this.customerName = this.accountLabel(account);
    this.hasCustomer = true;
    this.allReturns = [];
    this.filteredReturns = [];
    this.pagedReturns = [];
    this.basketItems = [];
    this.initBasketAndLoadReturns();
  }

  /**
   * Builds the result rows (with each account's property count) and opens the search
   * results modal. Picking a row + "View returns" opens that customer.
   */
  private openResultsModal(matches: AccountModel[], query: string): void {
    const rows$ = matches.map(a =>
      this.propertyService.getByAccount(a.id!).pipe(
        map(props => this.toResultRow(a, props?.length ?? 0)),
        catchError(() => of(this.toResultRow(a, 0)))
      )
    );

    forkJoin(rows$).subscribe({
      next: rows => {
        this.isSearching = false;
        const ref = this.dialog.open(SearchResultsDialogComponent, {
          data: { query, rows },
          autoFocus: false,
          maxWidth: '95vw',
        });
        ref.afterClosed().subscribe((account?: AccountModel) => {
          if (account) {
            this.selectAccount(account);
          }
        });
      },
      error: () => {
        this.isSearching = false;
        this.snackBar.open('Error loading search results. Please try again.', 'Dismiss', {
          duration: 5000,
          panelClass: ['snack-error']
        });
      }
    });
  }

  private toResultRow(a: AccountModel, propertyCount: number): SearchResultRow {
    return {
      account: a,
      id: a.id!,
      fullName: this.accountLabel(a),
      email: a.email || '',
      enrollmentType: this.resolveEnrollmentType(a.type),
      propertyCount,
    };
  }

  /** Resolve an account's type whether it arrives as an enum name, an int, or a label. */
  private resolveEnrollmentType(type: string | undefined): EnrollmentType | undefined {
    if (!type) {
      return undefined;
    }
    return (
      EnrollmentTypeStrMap.get(type) ||
      EnrollmentTypeIntMap.get(Number(type)) ||
      (Object.values(EnrollmentType).includes(type as EnrollmentType) ? (type as EnrollmentType) : undefined)
    );
  }

  clearCustomer(): void {
    this.hasCustomer = false;
    this.customerName = '';
    this.customerId = 0 as any;
    this.allReturns = [];
    this.filteredReturns = [];
    this.pagedReturns = [];
    this.basketItems = [];
    this.searchQuery = '';
    if (this.basketId !== null) {
      this.casheringService.deleteBasket(this.basketId).subscribe({
        error: () => { /* ignore */ }
      });
      this.basketId = null;
    }
    this.basketStore.clear();
  }

  /** Open a return in the editable detail modal (eye action). Reload returns after a save. */
  openReturnDetail(ret: ReturnSummaryDto): void {
    const ref = this.dialog.open(ReturnDetailComponent, {
      data: { returnId: ret.id, customerId: this.customerId, edit: true },
      width: '1200px',
      maxWidth: '96vw',
      autoFocus: false,
      panelClass: 'return-detail-dialog',
    });
    ref.afterClosed().subscribe((saved: boolean) => {
      if (saved) {
        this.loadOutstandingReturns();
      }
    });
  }

  // ===== Returns table pagination =====

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagedReturns();
  }

  /**
   * Year / Month / Status were bound in the template but never read, so the table always
   * showed every outstanding return regardless of what was selected. Filtering happens
   * here, against the already-loaded list - no refetch, so it applies on change rather
   * than only on a customer search.
   *
   * period is "YYYY-MM".
   */
  applyFilters(): void {
    const year = this.filterYear === '' ? null : String(this.filterYear);
    const monthIndex = this.filterMonth ? this.months.indexOf(this.filterMonth) : -1;
    const month = monthIndex >= 0 ? String(monthIndex + 1).padStart(2, '0') : null;

    this.filteredReturns = this.allReturns.filter(r => {
      const period = r.period ?? '';
      if (year && period.slice(0, 4) !== year) return false;
      if (month && period.slice(5, 7) !== month) return false;
      if (this.filterStatus && r.status !== this.filterStatus) return false;
      return true;
    });
    this.pageIndex = 0;
    this.updatePagedReturns();
  }

  private updatePagedReturns(): void {
    const start = this.pageIndex * this.pageSize;
    this.pagedReturns = this.filteredReturns.slice(start, start + this.pageSize);
  }

  // ===== Basket actions =====

  addToBasket(ret: ReturnSummaryDto): void {
    if (ret.rolledUp) {
      this.snackBar.open('This return is part of an installment plan, assessment, or lien — pay its plan return instead.',
        'Dismiss', { duration: 4000, panelClass: ['snack-error'] });
      return;
    }
    if (this.basketItems.some(b => b.id === ret.id)) {
      this.snackBar.open('This return is already in the basket.', 'Dismiss', {
        duration: 2500
      });
      return;
    }
    this.basketItems = [...this.basketItems, ret];
    // Keep selectedReturns in sync (used by payment processing)
    this.selectedReturns = [...this.basketItems];

    if (this.basketId !== null) {
      this.casheringService.addReturnToBasket(this.basketId, ret.id).subscribe({
        error: (err: any) => console.error('Error adding return to basket on backend:', err)
      });
    }
    this.persistBasket();

    this.snackBar.open('Return added to basket.', 'Dismiss', {
      duration: 2000,
      panelClass: ['snack-info']
    });
  }

  isInBasket(returnId: number): boolean {
    return this.basketItems.some(b => b.id === returnId);
  }

  // ===== Basket / payment init =====

  initBasketAndLoadReturns(): void {
    // Reuse the session basket when re-entering the same customer, so the basket
    // (and its items) is not recreated/lost on navigation.
    const saved = this.basketStore.get();
    if (saved && saved.customerId === this.customerId && saved.basketId !== null) {
      this.basketId = saved.basketId;
      this.basketItems = [...saved.items];
      this.selectedReturns = [...saved.items];
      if (!this.customerName) {
        this.customerName = saved.customerName;
      }
      this.loadOutstandingReturns();
      return;
    }

    // Switching to a different customer abandons any previous session basket.
    if (saved && saved.customerId !== this.customerId) {
      this.basketStore.clear();
    }

    const userId = this.getCurrentUserId();
    this.isCreatingBasket = true;
    this.hasLoadError = false;
    this.casheringService.createBasket(this.customerId, this.paymentType, '1', 'Main Office', userId).subscribe({
      next: (basket: BasketDto) => {
        this.basketId = basket.id;
        this.isCreatingBasket = false;
        this.loadOutstandingReturns();
      },
      error: (error: any) => {
        console.error('Error creating basket:', error);
        this.isCreatingBasket = false;
        // Fall back to loading returns without a basket
        this.loadOutstandingReturns();
      }
    });
  }

  loadOutstandingReturns(): void {
    this.isLoadingReturns = true;
    this.hasLoadError = false;
    this.casheringService.getOutstandingReturns(this.customerId).subscribe({
      next: (returns: ReturnSummaryDto[]) => {
        this.allReturns = returns;
        this.applyFilters();
        this.isLoadingReturns = false;
      },
      error: (error: any) => {
        console.error('Error loading outstanding returns:', error);
        this.isLoadingReturns = false;
        this.hasLoadError = true;
      }
    });
  }

  get isLoading(): boolean {
    return this.isCreatingBasket || this.isLoadingReturns || this.isSearching;
  }

  // ===== Basket item expansion =====

  toggleReturnExpanded(returnId: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.expandedReturns.has(returnId)) {
      this.expandedReturns.delete(returnId);
    } else {
      this.expandedReturns.add(returnId);
    }
  }

  isReturnExpanded(returnId: number): boolean {
    return this.expandedReturns.has(returnId);
  }

  onReturnCardKeydown(event: KeyboardEvent, returnId: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleReturnExpanded(returnId);
    }
  }

  // ===== Remove from basket =====

  confirmRemoveReturn(returnId: number): void {
    const dialogRef = this.dialog.open(RemoveFromCartDialogComponent, {
      width: '440px',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.basketItems = this.basketItems.filter(r => r.id !== returnId);
        this.selectedReturns = [...this.basketItems];
        // Also remove the item from the active basket on the backend
        if (this.basketId !== null) {
          this.casheringService.removeReturnFromBasket(this.basketId, returnId).subscribe({
            error: (err: any) => console.error('Error removing return from basket:', err)
          });
        }
        // Persist the smaller basket, or drop the session basket if it is now empty.
        if (this.basketItems.length > 0) {
          this.persistBasket();
        } else {
          this.basketStore.clear();
        }
        this.snackBar.open('Return removed from basket', 'Dismiss', {
          duration: 3000,
          panelClass: ['snack-info']
        });
      }
    });
  }

  // ===== Payment type change =====

  onPaymentTypeChange(): void {
    // When switching to Mail-in, remove non-check payment methods
    if (this.paymentType === PaymentType.MAIL_IN) {
      this.paymentMethods = this.paymentMethods.filter(
        m => m.type === PaymentMethodType.CHECK
      );
    }
  }

  // ===== Payment methods =====

  addPaymentMethod(type: PaymentMethodType): void {
    // When this is the only method, default its amount to the full amount due so the
    // cashier doesn't have to retype it. With split tender (2+ methods) each amount is
    // entered manually.
    const isSole = this.paymentMethods.length === 0;
    const defaultAmount = isSole ? Math.max(0, this.getTotalAmountDue()) : 0;

    const newMethod: PaymentMethodModel = {
      id: uuidv4(),
      type: type,
      amount: defaultAmount
    };

    if (type === PaymentMethodType.DEBIT_CARD) {
      // Card methods are always terminal-processed now (Catalis keeps its own fee) —
      // explicit 0, not undefined: the backend defaults a null fee % to 2%.
      newMethod.cardFeePercentage = 0;
      newMethod.cardFeeAmount = 0;
    }

    this.paymentMethods.push(newMethod);
  }

  removePaymentMethod(methodId: string | undefined): void {
    if (methodId) {
      this.paymentMethods = this.paymentMethods.filter(m => m.id !== methodId);
    }
  }

  updatePaymentMethodAmount(method: PaymentMethodModel, amount: number): void {
    method.amount = amount;

    if (method.type === PaymentMethodType.DEBIT_CARD && method.cardFeePercentage) {
      method.cardFeeAmount = (amount * method.cardFeePercentage) / 100;
    }
  }

  onAmountChanged(event: { method: PaymentMethodModel; amount: number }): void {
    this.updatePaymentMethodAmount(event.method, event.amount);
  }

  /** Replace a debit-card method with the gateway-enriched version (approved terminal txn). */
  onDebitMethodUpdated(updated: PaymentMethodModel): void {
    const index = this.paymentMethods.findIndex(m => m.id === updated.id);
    if (index !== -1) {
      this.paymentMethods[index] = updated;
    }
  }

  isPaymentMethodAllowed(type: PaymentMethodType): boolean {
    if (this.paymentType === PaymentType.MAIL_IN) {
      return type === PaymentMethodType.CHECK;
    }
    return true;
  }

  // ===== Totals =====

  getTotalAmountDue(): number {
    return this.basketItems.reduce((sum, ret) => sum + ret.totalAmountDue, 0);
  }

  getHasCredit(): boolean {
    return this.getTotalCredit() > 0;
  }

  getTotalCredit(): number {
    return this.basketItems
      .filter(r => r.totalAmountDue < 0)
      .reduce((sum, r) => sum + Math.abs(r.totalAmountDue), 0);
  }

  getTotalCardFees(): number {
    return this.paymentMethods
      .filter(m => m.type === PaymentMethodType.DEBIT_CARD)
      .reduce((sum, m) => sum + (m.cardFeeAmount || 0), 0);
  }

  getTotalPayment(): number {
    return this.paymentMethods.reduce((sum, method) => {
      const methodTotal = method.amount + (method.cardFeeAmount || 0);
      return sum + methodTotal;
    }, 0);
  }

  /** Full amount to collect at the register: tax due plus any card processing fees. */
  getAmountToCollect(): number {
    return this.getTotalAmountDue() + this.getTotalCardFees();
  }

  /**
   * Principal of non-cash methods (card + check), excluding card fees. Change is only
   * given in cash, so this may not exceed the amount due.
   */
  getNonCashPrincipal(): number {
    return this.paymentMethods
      .filter(m => m.type === PaymentMethodType.CHECK || m.type === PaymentMethodType.DEBIT_CARD)
      .reduce((sum, m) => sum + (m.amount || 0), 0);
  }

  /** Card/check tendered beyond the amount due — not allowed (no change on non-cash) unless kept as an over. */
  hasNonCashOverpay(): boolean {
    return this.getNonCashPrincipal() - Math.max(this.overShort ?? 0, 0) - this.getTotalAmountDue() > 0.005;
  }

  /** Tendered net of the cashier's over/short: what is actually applied plus change. */
  getEffectivePayment(): number {
    return this.getTotalPayment() - (this.overShort ?? 0);
  }

  getRemainingAmount(): number {
    const remaining = this.getAmountToCollect() - this.getEffectivePayment();
    return remaining > 0.005 ? remaining : 0;
  }

  /** Add an over/short pre-filled with the current difference (over = kept, short = accepted). */
  addOverShort(): void {
    // Pre-fill only a real difference; an exact tender starts blank so typing doesn't append to "0" (QA 2026-10-04).
    const diff = Math.round((this.getTotalPayment() - this.getAmountToCollect()) * 100) / 100;
    this.overShort = Math.abs(diff) > 0.005 ? diff : null;
    this.showOverShort = true;
  }

  removeOverShort(): void {
    this.overShort = null;
    this.showOverShort = false;
  }

  /** Error for the over/short entry, or null when it is within thresholds and balances. */
  getOverShortError(): string | null {
    const v = this.overShort;
    if (v == null) return null;
    if (v > this.overThreshold + 0.005) return `Over cannot exceed ${this.overThreshold.toFixed(2)}`;
    if (-v > this.underThreshold + 0.005) return `Short cannot exceed ${this.underThreshold.toFixed(2)}`;
    if (this.getRemainingAmount() > 0) return 'Payment plus over/short does not cover the amount due';
    return null;
  }

  /**
   * Cash change owed back to the customer when they tender more than is owed. This is NOT
   * an over/short — over/short is a drawer-close concept (declared cash vs expected). Card
   * fees are excluded; only cash overpayment produces change.
   */
  getCashChangeDue(): number {
    const change = this.getEffectivePayment() - this.getAmountToCollect();
    return change > 0.005 ? change : 0;
  }

  /** True when nothing but cash was tendered — the allowance is a till allowance. */
  isCashOnly(): boolean {
    return this.getNonCashPrincipal() <= 0.005;
  }

  /**
   * The shortfall the collector is willing to absorb. Card and check must still be exact:
   * a short there means the processor took a different amount, which is a reconciliation
   * problem rather than till rounding.
   */
  isShortfallAcceptable(): boolean {
    const remaining = this.getRemainingAmount();
    if (remaining === 0) return true;
    // An explicit over/short must balance on its own — no implicit allowance on top.
    if (this.overShort) return false;
    return this.isCashOnly() && remaining <= this.underThreshold + 0.005;
  }

  isExact(): boolean {
    return Math.abs(this.getEffectivePayment() - this.getAmountToCollect()) <= 0.005;
  }

  // ===== Validation =====

  canProcessPayment(): boolean {
    if (this.basketItems.length === 0) {
      return false;
    }
    // Zero return: nothing due (e.g. reported $0 on properties). File it with no payment
    // method — still processes and produces a receipt.
    if (this.getTotalAmountDue() <= 0 && this.paymentMethods.length === 0) {
      return true;
    }
    return (
      this.paymentMethods.length > 0 &&
      this.paymentMethods.every(m => m.amount >= 0) &&
      // Card methods are terminal-processed: not payable until the gateway has approved
      // the transaction and stamped a gatewayTransactionId on the method.
      this.paymentMethods.every(
        m => m.type !== PaymentMethodType.DEBIT_CARD || !!m.gatewayTransactionId
      ) &&
      // Must cover tax due + card fees, except that an all-cash payment may fall short by
      // up to the configured allowance; and no card/check overpayment.
      this.isShortfallAcceptable() &&
      !this.getOverShortError() &&
      !this.hasNonCashOverpay()
    );
  }

  // ===== Status badge helpers =====

  getStatusLabel(status: ReturnStatusEnum): string {
    switch (status) {
      case ReturnStatusEnum.UNPAID: return 'Unpaid';
      case ReturnStatusEnum.OVERDUE: return 'Overdue';
      case ReturnStatusEnum.PARTIALLY_PAID: return 'Partial';
      case ReturnStatusEnum.PENDING: return 'Pending';
      default: return status;
    }
  }

  getStatusIcon(status: ReturnStatusEnum): string {
    switch (status) {
      case ReturnStatusEnum.UNPAID: return 'warning';
      case ReturnStatusEnum.OVERDUE: return 'schedule';
      case ReturnStatusEnum.PARTIALLY_PAID: return 'pending';
      case ReturnStatusEnum.PENDING: return 'hourglass_empty';
      default: return 'info';
    }
  }

  getStatusClass(status: ReturnStatusEnum): string {
    switch (status) {
      case ReturnStatusEnum.UNPAID: return 'unpaid';
      case ReturnStatusEnum.OVERDUE: return 'overdue';
      case ReturnStatusEnum.PARTIALLY_PAID: return 'partial';
      case ReturnStatusEnum.PENDING: return 'pending';
      default: return 'unpaid';
    }
  }

  getPeriodYear(period: string): string {
    if (!period) return '—';
    const parts = period.split('-');
    return parts[0] || period;
  }

  getPeriodMonth(period: string): string {
    if (!period) return '—';
    const parts = period.split('-');
    if (parts.length < 2) return '—';
    const monthNum = parseInt(parts[1], 10);
    if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) return parts[1];
    return this.months[monthNum - 1];
  }

  // ===== Actions =====

  goBack(): void {
    if (this.currentView === 'basket') {
      this.currentView = 'returns';
    } else {
      this.router.navigate(['/app/search/customer', this.customerId]);
    }
  }

  /**
   * Cancels the payment being entered, not the basket. The trash icon on each row is the only
   * way a return leaves the basket.
   *
   * This used to empty basketItems/selectedReturns, and only those: the backend basket and the
   * session store still held the returns. So the list came back with "+" on rows that were still
   * in the basket, and navigating away and back (view drawer -> back) restored them from the
   * store with the counter reading 2 again.
   */
  cancel(): void {
    this.paymentMethods = [];
    this.removeOverShort();
    this.currentView = 'returns';
    if (this.basketItems.length > 0) {
      this.snackBar.open('Payment cancelled. The basket still holds your returns.', 'Dismiss', {
        duration: 4000,
        panelClass: ['snack-info']
      });
    }
  }

  processPayment(): void {
    if (!this.canProcessPayment() || this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    const userId = this.getCurrentUserId();

    if (this.basketId !== null) {
      this.addReturnsToBasketThenPay(userId);
    } else {
      this.submitPaymentRequest(userId);
    }
  }

  private addReturnsToBasketThenPay(userId: string | undefined): void {
    const returnIds = this.basketItems.map(r => r.id);
    const addNext = (index: number) => {
      if (index >= returnIds.length) {
        const methodDtos = this.paymentMethods.map(m => ({
          type: m.type,
          amount: m.amount,
          checkNumber: m.checkNumber,
          cardLastFour: m.cardLastFour,
          cardType: m.cardType,
          cardFeePercentage: m.cardFeePercentage,
          cardFeeAmount: m.cardFeeAmount,
          gatewayTransactionId: m.gatewayTransactionId
        }));
        this.casheringService.calculatePaymentTotals(this.basketId!, methodDtos).subscribe({
          next: () => this.submitPaymentRequest(userId),
          error: (err: any) => {
            console.error('Error calculating basket totals:', err);
            this.isProcessing = false;
            this.snackBar.open('Error calculating totals. Please try again.', 'Dismiss', {
              duration: 5000,
              panelClass: ['snack-error']
            });
          }
        });
        return;
      }
      this.casheringService.addReturnToBasket(this.basketId!, returnIds[index]).subscribe({
        next: () => addNext(index + 1),
        error: (err: any) => {
          // 409 = return already in the basket (it was persisted when added via
          // addToBasket). That is the expected state here, so treat it as success
          // and continue instead of aborting the whole payment.
          if (err?.status === 409) {
            addNext(index + 1);
            return;
          }
          console.error('Error adding return to basket:', err);
          this.isProcessing = false;
          this.snackBar.open('Error preparing payment. Please try again.', 'Dismiss', {
            duration: 5000,
            panelClass: ['snack-error']
          });
        }
      });
    };
    addNext(0);
  }

  private submitPaymentRequest(userId: string | undefined): void {
    // Strip the client-side UUID `id` from each method — the backend
    // PaymentMethodDto.id is a Long, so sending the UUID fails deserialization.
    const paymentMethodDtos = this.paymentMethods.map(m => ({
      type: m.type,
      amount: m.amount,
      checkNumber: m.checkNumber,
      cardLastFour: m.cardLastFour,
      cardType: m.cardType,
      cardFeePercentage: m.cardFeePercentage,
      cardFeeAmount: m.cardFeeAmount,
      gatewayTransactionId: m.gatewayTransactionId
    }));

    const paymentRequest: PaymentRequestDto = {
      accountId: this.customerId,
      paymentType: this.paymentType,
      returnIds: this.basketItems.map(r => r.id),
      paymentMethods: paymentMethodDtos as any[],
      cashierUserId: userId,
      overShortAdjustment: this.overShort || undefined
    };

    this.submitPayment(paymentRequest, userId);
  }

  /**
   * Submits the payment request. If the backend rejects it with 409 DRAWER_CLOSED
   * (the cashier has no open drawer), prompt to open a drawer and, on success,
   * retry the same payment once. Any other error falls through to the normal
   * error handling. A second failure surfaces normally (no infinite loop).
   */
  private submitPayment(paymentRequest: PaymentRequestDto, userId: string | undefined): void {
    // Record the actual cashier on the payment (X-User-Name). Without this the backend
    // defaults cashierName to "System", which then shows in the payment reports.
    const userName = this.authService.getCurrentUser()?.username;
    this.casheringService.processPayment(paymentRequest, userId, userName).subscribe({
      next: payment => {
        // Basket consumed by the payment — drop the session basket and its reference.
        this.basketStore.clear();
        this.basketId = null;
        this.basketItems = [];
        this.isProcessing = false;
        this.router.navigate(['/app/cashering', this.customerId, 'receipt', payment.id]);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 409 && error.error?.code === 'DRAWER_CLOSED') {
          this.dialog.open(OpenDrawerDialogComponent, { width: '480px' }).afterClosed().subscribe(body => {
            if (body) {
              this.drawerSvc.open(body).subscribe({
                next: () => {
                  this.loadDrawer();
                  this.submitPayment(paymentRequest, userId); // retry once
                },
                error: () => {
                  this.isProcessing = false;
                  this.snackBar.open('Could not open the cash drawer. Please try again.', 'Dismiss', {
                    duration: 5000,
                    panelClass: ['snack-error']
                  });
                }
              });
            } else {
              // Cashier dismissed the open-drawer dialog: abandon this payment attempt.
              this.isProcessing = false;
            }
          });
          return;
        }
        if (error.status === 409 && error.error?.code === 'OUT_OF_ORDER') {
          const blocking = (error.error?.blockingReturns || [])
            .map((b: any) => b.period).filter(Boolean).join(', ');
          this.isProcessing = false;
          this.snackBar.open('Returns must be paid oldest first. Older past-due return(s) '
            + (blocking ? '(' + blocking + ') ' : '') + 'must be included in this payment.', 'Dismiss', {
            duration: 8000,
            panelClass: ['snack-error']
          });
          return;
        }
        console.error('Error processing payment:', error);
        this.isProcessing = false;
        this.snackBar.open('Payment could not be processed. Please try again.', 'Dismiss', {
          duration: 5000,
          panelClass: ['snack-error']
        });
      }
    });
  }
}
